import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Flag as FlagIcon,
  LockOutlined as LockOutlinedIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

import { DEFAULT_CAPACITY_RULES, taskProjectLabel } from '../../utils/capacityCalc';
import { dagenOverDeadline, isHardeDeadline, LANES, werkdagenInBereik } from '../../utils/dagstartLanes';
import { parseISODate } from '../../utils/weekUtils';
import { maatVoor } from './dichtheden';

import { PRIO_COLORS, TYPE_COLORS } from '../../utils/taakKleuren';

/** Alias voor bestaande importeurs — de bron is nu utils/taakKleuren.js. */
export const PRIO_KLEUREN = PRIO_COLORS;
const TYPE_KLEUREN = TYPE_COLORS;

/**
 * Afspraken uit Outlook krijgen hun kleur van wie er aanschuift, niet van hun prio —
 * die hebben ze niet. Eén paarse lane las als "deze persoon vergadert de hele dag",
 * terwijl de helft van de blokken zelf-geplande werktijd was ("Taak - mail", "inlezen
 * omring"). Nu zie je in één oogopslag het verschil tussen klantcontact, intern overleg
 * en iemand die tijd voor zichzelf blokt.
 */
export const AFSPRAAK_KLEUREN = {
  extern: '#0284c7',  // klant of leverancier aan tafel
  intern: '#a855f7',  // collega's onderling — de bestaande afspraakkleur
  taak: '#64748b',    // niemand anders uitgenodigd: geblokte werktijd
};

/** Kort soort-label voor de tooltip — de uitleg staat al in de legenda onder de lane. */
export const AFSPRAAK_SOORT_TEKST = {
  extern: 'extern',
  intern: 'intern',
  taak: 'eigen tijd',
};

export const taakKleur = (taak) =>
  (taak.type === 'Afspraak' && AFSPRAAK_KLEUREN[taak.afspraakSoort])
  || PRIO_KLEUREN[taak.prio] || TYPE_KLEUREN[taak.type] || '#94a3b8';

/** Hoogtes per dichtheidsstand — zie `dichtheden.js` voor de hele schaal. */
export const blokHoogte = (dichtheid) => maatVoor(dichtheid).blok;

/**
 * Eén taak op de tijdlijn — bewust één regel.
 *
 * De oude kaart had drie regels (naam, uren · prio · dagen, PL) en kostte daarmee
 * 44px per taak; bij twaalf taken zie je nog geen halve week. Type, duur-in-tekst en
 * projectleider staan nu in de tooltip en het detailpaneel, waar je ze pas nodig hebt
 * als je ergens op doorvraagt.
 */
const kortDatum = (iso) => {
  const d = parseISODate(String(iso || '').slice(0, 10));
  return d ? d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '') : '';
};

/**
 * Wat er tijdens het gebaar op het blok staat.
 *
 * Bij resizen is dat de urenverdeling, en dat is geen luxe: de capaciteitsberekening
 * verdeelt de uren over de werkdagen in het bereik. Rek je een taak van 6 uur van twee
 * naar drie dagen, dan zakt de dagbelasting van 3 naar 2 uur — zonder dit label ziet de
 * planner de capaciteitsrij springen zonder te weten waarom. Bij verslepen verandert de
 * duur niet en is de nieuwe datum het enige dat telt.
 */
export function sleepLabelVoor(taak, stand) {
  if (!stand) return null;
  if (stand.soort === 'verplaats') return `${stand.kopie ? 'kopie ' : ''}→ ${kortDatum(stand.startDate)}`;
  const dagen = Math.max(1, werkdagenInBereik(stand.startDate, stand.endDate).length);
  const dagWoord = dagen === 1 ? 'dag' : 'dgn';
  if (!(taak.hours > 0)) return `${dagen} ${dagWoord}`;
  const perDag = Math.round((taak.hours / dagen) * 10) / 10;
  return `${taak.hours}u / ${dagen} ${dagWoord} = ${perDag}u p.d.`;
}

/**
 * Staan er geen uren bij deze taak — en is dat een gat, geen bewuste regel?
 *
 * De capaciteitsberekening vult een leeg `UrenWerk` stil aan met `fallbackHours`
 * (standaard 4). Dat is een redelijke gok voor één taak, maar hij stapelt: op 18 augustus
 * stonden bij Bram vier ondertitel-taken zonder uren, samen goed voor 16 van zijn 25 uur
 * — 312% voor werk dat in de praktijk een ochtend is. Zolang zo'n blok er precies
 * hetzelfde uitziet als een taak waar wél uren bij staan, is dat cijfer niet te herleiden
 * en niet te repareren.
 *
 * Types met een eigen inschatting (draaidag, vrije dag, vanallesuurtje) tellen niet als
 * gat: daar ís de waarde de regel. Afspraken evenmin — hun duur komt uit de agenda.
 */
export function ureninschattingOntbreekt(taak, regels) {
  if (!taak || taak.type === 'Afspraak') return false;
  if (Number(taak.hours) > 0) return false;
  const perType = regels?.hoursByType || DEFAULT_CAPACITY_RULES.hoursByType;
  return perType[taak.type] === undefined;
}

/**
 * Een dun handvat op de rand van het blok waarmee je de duur verandert. Alleen zichtbaar
 * bij hover: op een gedeeld scherm is een blok met twee permanente grijpranden onrustig,
 * en de cursor vertelt hetzelfde verhaal zodra je in de buurt komt.
 */
function ResizeHandvat({ kant, onPointerDown }) {
  return (
    <Box
      onPointerDown={onPointerDown}
      className="dagstart-resize"
      aria-hidden
      sx={{
        position: 'absolute', top: 0, bottom: 0, [kant]: 0, width: 7,
        cursor: 'ew-resize', opacity: 0, transition: 'opacity 0.12s ease',
        // Een streepje in het midden, zodat je ziet dát je hier kunt pakken.
        '&::after': {
          content: '""', position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 2, height: '55%', borderRadius: 1, backgroundColor: '#64748b',
        },
      }}
    />
  );
}

export default function TaskBlock({
  taak,
  lane,
  dichtheid = 'comfortabel',
  vandaagISO,
  onKlik,
  onAfvinken,
  onNaarVandaag,
  sleepbaar = false,
  onGebaar,
  onToets,
  sleepStand = null,
  regels,
  onPrioMenu,
}) {
  const kleur = taakKleur(taak);
  const maat = maatVoor(dichtheid);
  const isAfspraak = taak.type === 'Afspraak';
  const urenOntbreken = ureninschattingOntbreekt(taak, regels);
  const aanname = regels?.fallbackHours || DEFAULT_CAPACITY_RULES.fallbackHours;

  const project = taskProjectLabel(taak);
  const overDeadline = dagenOverDeadline(taak, vandaagISO);
  const deadlineVerlopen = overDeadline > 0 && isHardeDeadline(taak.deadlineType);
  const achterstallig = lane === LANES.ACHTERSTALLIG;
  const ongepland = lane === LANES.ONGEPLAND;

  // Compact: geen herhaalde/uitleggerige regels (feedback 31 aug). Afspraken
  // tonen tijd + kort soort-label; Type/Prio alleen voor échte taken (bij
  // afspraken is dat drie keer hetzelfde woord).
  const tooltip = [
    taak.name,
    isAfspraak && [
      taak.startTime && `${taak.startTime}${taak.endTime ? `–${taak.endTime}` : ''}`,
      AFSPRAAK_SOORT_TEKST[taak.afspraakSoort],
    ].filter(Boolean).join(' · '),
    taak.prive && 'Privé — alleen de tijd is zichtbaar',
    !taak.prive && taak.afgeschermd && 'Titel afgeschermd — de tijd telt gewoon mee',
    project && `📁 ${project}`,
    !isAfspraak && taak.type && `Type: ${taak.type}`,
    !isAfspraak && taak.prio && taak.prio !== taak.type && `Prio: ${taak.prio}`,
    taak.hours > 0 && `${taak.hours} uur`,
    urenOntbreken && (taak.startDate
      ? `❓ Geen uren ingevuld — de capaciteit rekent met ${aanname} uur. Vul UrenWerk in Airtable in.`
      : '❓ Geen uren ingevuld — vul UrenWerk in Airtable in.'),
    taak.deadline && `Deadline: ${taak.deadline}${taak.deadlineType ? ` (${taak.deadlineType})` : ''}`,
    deadlineVerlopen && `⚑ Deadline ${overDeadline} ${overDeadline === 1 ? 'werkdag' : 'werkdagen'} verstreken`,
    taak.projectleider && `PL: ${taak.projectleider}`,
    taak.feedbackOntbreekt && '💬 Feedback ontbreekt — v2/DEF zonder link of opmerkingen van de klant (PL)',
  ].filter(Boolean).join('\n');

  const bezig = Boolean(sleepStand);

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>{tooltip}</Box>}
      arrow
      enterDelay={400}
      // Tijdens een gebaar zou de tooltip midden onder je cursor openklappen en het blok
      // dat je aan het verslepen bent afdekken.
      disableHoverListener={bezig}
    >
      <Box
        // Bij een sleepbaar blok handelt het pointer-gebaar zélf de klik af: onder 4px
        // verplaatsing is het een klik, daarboven een sleep. Twee losse handlers zouden
        // bij elke sleep óók het detailpaneel openen.
        onClick={sleepbaar ? undefined : onKlik}
        onPointerDown={sleepbaar ? (e) => onGebaar?.(e, 'verplaats') : undefined}
        // Rechtsklik = prioriteit wijzigen zonder de taak te openen. Afspraken uit
        // Outlook hebben geen prioriteit, dus daar bieden we het niet aan.
        onContextMenu={onPrioMenu && !isAfspraak ? onPrioMenu : undefined}
        // Focusbaar, dus met Tab bereikbaar en met pijltjes te verzetten. Op een gedeeld
        // scherm is dat vaak prettiger dan slepen: je hoeft je muis niet te laten volgen.
        tabIndex={0}
        role="button"
        aria-label={`${taak.name}${taak.startDate ? `, ${kortDatum(taak.startDate)}` : ''}`}
        onKeyDown={onToets ? (e) => onToets(e, taak) : undefined}
        sx={{
          position: 'relative',
          '&:focus-visible': {
            outline: `2px solid ${kleur}`,
            outlineOffset: 1,
          },
          height: maat.blok,
          mx: 0.25,
          my: '2px',
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          borderRadius: 0.75,
          cursor: sleepbaar ? (bezig ? 'grabbing' : 'grab') : 'pointer',
          // Zonder dit selecteert een sleepbeweging de taaknaam als tekst.
          userSelect: sleepbaar ? 'none' : undefined,
          touchAction: sleepbaar ? 'none' : undefined,
          // 16% tint (was 8%): Prio 1/2/3 waren naast elkaar nauwelijks uit elkaar te
          // houden (tool-feedback #59 "contrast iets verhogen").
          backgroundColor: `${kleur}29`,
          // Ongepland heeft geen datums; de stippellijn zegt dat het blok op deze
          // plek staat omdat de deadline daar ligt, niet omdat het werk daar staat.
          border: ongepland ? `1px dashed ${kleur}` : '1px solid transparent',
          borderLeft: `3px solid ${kleur}`,
          transition: bezig ? 'none' : 'background-color 0.12s ease',
          ...(bezig && {
            backgroundColor: `${kleur}4d`,
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.18)',
            outline: `1px solid ${kleur}`,
          }),
          '&:hover': { backgroundColor: `${kleur}40` },
          '&:hover .dagstart-acties': { opacity: 1 },
          '&:hover .dagstart-resize': { opacity: 0.55 },
        }}
      >
        {sleepbaar && <ResizeHandvat kant="left" onPointerDown={(e) => onGebaar?.(e, 'links')} />}
        {sleepbaar && <ResizeHandvat kant="right" onPointerDown={(e) => onGebaar?.(e, 'rechts')} />}

        {!isAfspraak && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onAfvinken?.(taak); }}
            // Ook pointerdown stoppen: het sleep-gebaar op het blok telt een
            // stilstaande pointerup als 'klik' en opende dan alsnog het paneel.
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`${taak.name} afronden`}
            sx={{ p: 0, color: '#cbd5e1', '&:hover': { color: '#22c55e' } }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: maat.icoon }} />
          </IconButton>
        )}
        {isAfspraak && taak.startTime && (
          <Typography sx={{ fontSize: maat.detail, fontWeight: 700, color: kleur, flexShrink: 0 }}>
            {/* Eindtijd erbij: zonder duur zag je niet dat een meeting 3 uur kost. */}
            {taak.startTime}{taak.endTime ? `–${taak.endTime}` : ''}
          </Typography>
        )}

        {taak.afgeschermd && (
          <LockOutlinedIcon
            aria-label={taak.prive ? 'Privé-afspraak' : 'Afgeschermd onderwerp'}
            sx={{ fontSize: maat.icoon - 2, color: '#94a3b8', flexShrink: 0 }}
          />
        )}
        {/* Prefix (nummer · klant) en taaknaam apart afkappen: één noWrap-regel
            liet de klantnaam de hele breedte opeten en kapte juist de taaknaam
            af ("Licenties contr…" — Bram, 31 aug). De prefix mag max ~45%. */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', flex: 1, minWidth: 0, gap: 0.4 }}>
          {project && (
            <Typography noWrap sx={{ fontSize: maat.taak, fontWeight: 700, color: '#4b5563', flexShrink: 1, minWidth: 0, maxWidth: '45%' }}>
              {project} ·
            </Typography>
          )}
          <Typography noWrap sx={{ fontSize: maat.taak, color: '#1f2937', flex: 1, minWidth: 0 }}>
            {taak.name}
          </Typography>
        </Box>

        {bezig ? (
          <Typography sx={{
            fontSize: maat.detail, fontWeight: 800, color: kleur,
            flexShrink: 0, whiteSpace: 'nowrap',
          }}>
            {sleepLabelVoor(taak, sleepStand)}
          </Typography>
        ) : taak.hours > 0 ? (
          <Typography sx={{ fontSize: maat.detail, color: '#6b7280', flexShrink: 0 }}>
            {taak.hours}u
          </Typography>
        ) : urenOntbreken && (
          /* Waar normaal "4u" staat, staat hier een amber vraagteken. Bewust gevuld en
             niet grijs: een blok zonder uren zag er hiervoor exact hetzelfde uit als een
             blok mét, terwijl de capaciteit er wél {aanname} uur voor rekende. Dat cijfer
             moet je in een dagstart kunnen aanwijzen — en aan het eind van de rit hoort
             deze markering leeg te lopen doordat de uren ingevuld wórden. */
          <Box
            component="span"
            aria-label={`Geen uren ingevuld, gerekend met ${aanname} uur`}
            sx={{
              flexShrink: 0,
              minWidth: maat.icoon + 1,
              height: maat.icoon + 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#fbbf24',
              color: '#78350f',
              border: '1px solid #d97706',
              fontSize: maat.detail,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            ?
          </Box>
        )}

        {/* Een verstreken harde deadline is een vlaggetje, geen badge: de tekst "29d over"
            kostte breedte op elk blok terwijl het meestal om een deadline gaat die in
            Airtable simpelweg niet is bijgesteld. Het aantal dagen staat in de tooltip. */}
        {/* v2/DEF zonder feedback: amber tekstballon. De PL hoort dit vóór de start te vullen
            (todo op Vandaag); de editor ziet hier dat hij nog niet kan beginnen. */}
        {taak.feedbackOntbreekt && !sleepStand && (
          <Box component="span" aria-label="Feedback ontbreekt"
            sx={{ flexShrink: 0, fontSize: maat.detail, lineHeight: 1, filter: 'saturate(1.2)' }}>💬</Box>
        )}

        {deadlineVerlopen && (
          <FlagIcon
            aria-label={`Deadline ${overDeadline} werkdagen verstreken`}
            sx={{ fontSize: maat.icoon - 3, color: '#dc2626', flexShrink: 0 }}
          />
        )}

        {(achterstallig || ongepland) && (
          <Box className="dagstart-acties" sx={{ opacity: 0, transition: 'opacity 0.12s ease', flexShrink: 0 }}>
            <Tooltip title="Naar vandaag" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onNaarVandaag?.(taak); }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={`${taak.name} naar vandaag verzetten`}
                sx={{ p: 0, color: '#2563eb' }}
              >
                <TodayIcon sx={{ fontSize: maat.icoon }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
